
import { Component, Vue, Watch } from "vue-property-decorator";
import { namespace, Action, Getter } from "vuex-class";
import { FeaturesList, LoginResult, LoginStatus, LoginPreferences, UserPermissions } from "@/store/types";

import { Domain } from "@/store/user-management/types";
import api, { UserCredentials } from "@/services/api.service";
import mobileApi from "@/services/api.mobile.service";
import vselect3 from "vselect3";
import { throttle, debounce } from "lodash";
import { stringMixin } from "@/mixins";
import { getSsoURL, ssoProvider } from "@/utils/singleSignOnHelper";
import ResetPasswordModal from "@/components/ResetPasswordModal.vue";
import RenewPasswordModal from "@/components/RenewPasswordModal.vue";
import SOCContactModal from "@/components/SOCContactModal.vue";
import router, { findRouteByKey, RouteConfigX, getLandingPages } from '@/router';
import { get } from "lodash";

const License = namespace("license");
const { isNullOrWhitespace } = stringMixin.methods;

@Component({
	components: {
		"v-select": vselect3,
		"reset-password-modal": ResetPasswordModal,
		"renew-password-modal" : RenewPasswordModal,
		"soc-contact-modal" : SOCContactModal
	}
})
export default class Login extends Vue {
	@Getter("getVersion") private webVersion;
	@Getter("getServerName") private serverName;
	@Getter getFeature: (featureName: string[]) => boolean;
	@Getter("getFeaturesList") private featuresList: FeaturesList;
	@Getter("accountCreationEnabled") private accountCreationAllowed;
	@Getter("loginPreferences") private loginPreferences: LoginPreferences;
	@Getter getPermissions: UserPermissions;

	@Action("login") private loginUser: ({ username, password }) => Promise<LoginResult>;
	@Action private loadAccountCreationEnabled: () => Promise<void>;
	@Action("loadLoginPreferences") private getLoginPreferences: () => Promise<void>;
	@Action("clearLoginPreferences") private clearLoginPreferences: () => Promise<void>;
	@Action("loadFeaturesList") private loadFeaturesList: () => Promise<void>;

	@License.Action private fetchLicenseDetails: (tenantGroupId?: number) => Promise<void>;

	public user: string = "";
	public password: string = "";
	public loggingIn: boolean = false;
	public redirectPath: string = "";
	private errorMessage: string = "";

	private domains: Domain[] = [];
	private selectedDomain: Domain = null;
	private showPasswordResetModal: boolean = false;
	private showPasswordRenewModal: boolean = false;
	private showSocCallModal: boolean = false;

	private SocTelephoneNumber: string | null = null;

	public ssoProviderName: string = "";

	private readonly domainLoginRegex = /^([a-zA-Z][a-zA-Z0-9\-\.]{0,61}[a-zA-Z])\\(\w[\w\.\- ]*)$/g;

	private ssoWindow: Window = null; 

	/**
	 * Returns true if the domain select is enabled and should be shown.
	 */
	private get showDomainSelect() {
		return this.loginPreferences?.domainLoginSelectEnabled ?? false;
	}

	/**
	 * Returns true if domain select is enabled, and users are allowed to login without a domain.
	 */
	private get allowNoDomain() {
		return this.showDomainSelect && (this.loginPreferences?.domainLoginAllowNoDomain ?? false);
	}

	/**
	 * Returns true if the Soc Telephone number was returned and the user is on mobile.
	 */
	private get showSocCallButton() {
		return (this.SocTelephoneNumber ? true : false) && /Mobi|Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
	}

	/**
	 * Returns the user's username, including the domain (if applicable).
	 */
	private get userName() {
		// If domain select is disabled, there is no selected domain, or the username is empty,
		// return just the username.
		if (
			!this.showDomainSelect ||
			isNullOrWhitespace(this.selectedDomain?.domainName) ||
			isNullOrWhitespace(this.user)
		) {
			return this.user;
		}

		return `${this.selectedDomain.domainName}\\${this.user}`;
	}

	/**
	 * Mounted hook.
	 */
	public async mounted() {
		// Get the telephone number for the security operations center call button.
        this.SocTelephoneNumber = await mobileApi.getSocTelephone();	// Will only return if the system has mobile and mobile/call features enabled.

		await this.loadAccountCreationEnabled();
		await this.loadLoginPreferences();

		if (!this.loginPreferences){
			return;
		}

		this.ssoProviderName = ssoProvider[this.loginPreferences.ssoProviderSettings.providerType ?? 0];

		// Check for Pref login button string overide
		if (this.loginPreferences.ssoLoginButtonString !== undefined && this.loginPreferences.ssoLoginButtonString !== null) {
			this.ssoProviderName = this.loginPreferences.ssoLoginButtonString;
		}
		else {
			this.ssoProviderName += " Login";
		}

		// Check for SSO auto-redirect
		if (this.loginPreferences.ssoProviderSettings.ssoEnabled && 
			this.loginPreferences.ssoProviderSettings.ssoAutoRedirect && 
			this.loginPreferences.ssoProviderSettings.providerType === ssoProvider.Okta) {

			let oktaSsoAutoLoggedIn = "";
			let data = sessionStorage.getItem('OktaSsoLoggedIn');
			if (data !== undefined && data !== null) {
				oktaSsoAutoLoggedIn = data;
			}

			if (oktaSsoAutoLoggedIn !== "true") {
				await this.ssoLogin();
				sessionStorage.setItem('OktaSsoLoggedIn', 'true');
			}
		}
	}

	/**
	 * Loads preferences for the login page, and if enabled, the list of login domains.
	 */
	private async loadLoginPreferences() {
		try {
			// Load login prefs.
			await this.getLoginPreferences();

			// Check to see if we need to load the list of domains.
			if (this.showDomainSelect) {
				this.domains = await api.getAvailableDomains();

				if (!this.domains || this.domains.length == 0) {
					// If we haven't got any domains configured, show a warning message and
					// hide the domain select.
					this.$notify({
						title: "Login",
						text: "Unable to load list of login domains.",
						type: "warning"
					});

					this.loginPreferences.domainLoginSelectEnabled = false;
				} else if (this.domains.length > 0 && !this.allowNoDomain) {
					// Otherwise, if we do have domain entries, and the user is *required*
					// to use a domain login, auto-select the first one.
					this.selectedDomain = this.domains[0];
				}
			}
		} catch (err) {
			await this.clearLoginPreferences();
			console.error("Unable to load login prefs, domain select will not be available.");
		}
	}

	/**
	 * Performs user login, handling re-directs and any errors that may be returned
	 * from the data service.
	 */
	public async login(userCredentialsIn: UserCredentials = null) {
		this.loggingIn = true;

		// Save our redirect path once we've successfully logged in
		if (this.$route.query.redirect && this.$route.query.redirect !== "") {
			this.redirectPath = this.$route.query.redirect.toString();
		}

		try {
			let userCredentials: UserCredentials = {
				username: this.userName,
				password: this.password
			};

			if (userCredentialsIn !== null) {
				userCredentials = userCredentialsIn;
			}

			let loginResult = await this.loginUser(userCredentials);

			this.loggingIn = false;
			var path = "event-queue";

			// If we saved a redirect when this page was first loaded, now we've logged in, go to the redirect path
			if (this.redirectPath && this.redirectPath !== "") {
				path = this.redirectPath;
			} else if (loginResult.redirectRoute) {
				path = loginResult.redirectRoute;
			}

			await this.fetchLicenseDetails();

			await this.routeOnLogin(path, loginResult.landingPage);
		} catch (errorResult) {
			this.loggingIn = false;
			if (typeof errorResult.status == "undefined") {
				this.errorMessage = "Error establishing connection to SureView";
			} else if (errorResult.error != null && errorResult.error !== "") {
				this.errorMessage = errorResult.error;
			} else {
				switch (errorResult.status) {
					case LoginStatus.InvalidPassword:
						this.errorMessage = "Invalid username or password.";
						break;
					case LoginStatus.LockedOut:
						this.errorMessage = "You have been locked out due to too many password attempts.";
						break;
					case LoginStatus.DisabledUser:
						this.errorMessage = "Your account has been disabled.";
						break;
					case LoginStatus.PasswordExpired:
						this.errorMessage = "Your Password has expired. Please update your password.";
						this.showPasswordRenewModal = true;
						break;
					default:
						this.errorMessage = "Error during login";
				}
			}
		}
	}

	private async routeOnLogin(fallback: string, landingPages: string[]) {
		// Check if we landing page ff on.
		if (!this.getFeature(['Users', 'LandingPage'])) {
			await this.$router.push(fallback);
			return;
		}

		const validRoute = getLandingPages(true, null, false)
			.filter(x => landingPages.includes(x.meta.key))
			.sort((a, b) => landingPages.indexOf(a.meta.key) - landingPages.indexOf(b.meta.key))[0];

		// Check if we haven't permisions to view this route.
		if (!validRoute) {
			await this.$router.push(fallback);
			return;
		}

		await this.$router.push({ name: validRoute.name });
	}

	/**
	 * Watches for when the username field is changed. If the domain select dropdown is enabled,
	 * it checks to see if the user has entered a domain as part of their username. If they have,
	 * it parses the domain, sets it in the select, and removes it from the username field.
	 */
	@Watch("user")
	private async onUserChanged(newValue: string, oldValue: string) {
		if (newValue == oldValue || !this.showDomainSelect) {
			return;
		}
		this.tryParseUsernameDomain(newValue);
	}

	/**
	 * Parses the domain from the current username (if there is one), and updates the dropdown and
	 * user name fields. Throttled to prevent us from doing too many regex matches.
	 */
	private tryParseUsernameDomain = throttle(
		function(username: string) {
			// Match the username field against the domain login regex
			const matchesIterator = username.matchAll(this.domainLoginRegex);
			const matches = Array.from(matchesIterator)[0];

			if (!matches || matches.length == 0) {
				return null;
			}

			// Pull out the domain name and user name capture group values
			const [_, domainName, userName] = matches;

			// We've got a domain name, try to find it in the list of valid domains.
			let domain = this.domains.find(d => d.domainName.toLowerCase() == domainName.toLowerCase());

			// If we can't find the domain in the list, create a model for it. We do this because we
			// still want users to be able to try the login, even if the domain may not exist.
			if (!domain) {
				domain = {
					domainId: -1,
					domainName
				};
			}

			// Populate the dropdown and update the user name to remove the 'domain\' part.
			this.selectedDomain = domain;
			this.user = userName;
		},
		300,
		{ leading: false, trailing: true }
	);

	/**
	 * Takes the user to the signup page.
	 */
	private async goToSignup() {
		await this.$router.push("signup");
	}

	/**
	 * Handler for the form submit event, prevents default behaviour.
	 */
	private onSubmit() {
		return false;
	}

	/**
	 * Takes the user to the SSO provider login page.
	 */
	private async ssoLogin() {
		if (this.loginPreferences.ssoProviderSettings.providerType === ssoProvider.OpenId) {
			window.location.href = await getSsoURL(this.loginPreferences.ssoProviderSettings);
		} else if (this.loginPreferences.ssoProviderSettings.providerType === ssoProvider.Okta) {
			let request = await api.addOktaRequest(window.location.href); 
			if (request.data.successful === false) {
				console.error(request.data.error)
				return;
			}

			if (this.ssoWindow !== null) {
				return;
			}

			// Open the SSO endpoint
			this.ssoWindow = window.open(request.data.url, '_self');
		}
	}

	/**
	 * Open password reset modal.
	 */
	private resetPassword(): void {
		this.showPasswordResetModal = true;
	}

	/**
	 * Logs the user in with their renewed password after a succesfull expired password renewal from the renew-password-modal
	 */
	private renewedLogin( renewedCredentials : UserCredentials): void {
		this.user = renewedCredentials.username;
		this.password = renewedCredentials.password;
		this.login();
	}

}
